import React, { useState, useEffect } from "react"; // 1. Added useEffect
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import LoginImage from "../assets/login.jpg";
import SummaryApi from "../common";

import "react-toastify/dist/ReactToastify.css";

const Login = () => {
  const [isRegister, setIsRegister] = useState(false);
  
  // Form States
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // UI States
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  // ------------------ Scroll to Top Logic ------------------
  // This runs when the component mounts and whenever the view changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth", // Use "auto" for instant jump, "smooth" for scrolling animation
    });
  }, [isRegister, showOtpScreen]); // Depend on these states to scroll up when they change

  // ------------------ Helper: Fetch & Display OTP ------------------
  const fetchAndDisplayOtp = async (mobileNumber) => {
    try {
      const response = await fetch(SummaryApi.customerOtp.url, {
        method: SummaryApi.customerOtp.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: SummaryApi.bearerToken,
        },
        body: JSON.stringify({
          mobile_number: mobileNumber,
        }),
      });

      const data = await response.json();

      if (data.status || data.success) {
        let otpCode = data.otp || data.data?.otp;
        if (!otpCode && typeof data.message === 'string' && data.message.match(/\d{4,6}/)) {
           otpCode = data.message.match(/\d{4,6}/)[0];
        }

        if (otpCode) {
          toast.info(`Your OTP is: ${otpCode}`, {
            position: "top-center",
            autoClose: 10000,
          });
        } else {
          toast.success("OTP sent to your mobile.");
        }
      } else {
        toast.error(data.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error("Error fetching OTP:", err);
      toast.error("Error sending OTP");
    }
  };

  // ------------------ Login ------------------
  const handleLogin = async (e) => {
    e.preventDefault();
    const mobileRegex = /^\d{10}$/;

    if (!mobileRegex.test(mobile)) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    if (!password) {
      toast.error("Enter your password");
      return;
    }
    setIsLoading(true);

    try {
      const response = await fetch(SummaryApi.customerLogin.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: SummaryApi.bearerToken,
        },
        body: JSON.stringify({ mobile_number: mobile, password }),
      });

      const data = await response.json();

      if (data.success) {
        if (data.is_otp_verified === 1) {
          sessionStorage.setItem("customer_id", data.customer_id);
          if (data.full_name) sessionStorage.setItem("customer_name", data.full_name);
          
          toast.success("Login successful!");
          setTimeout(() => navigate("/"), 500);
        } else {
          toast.info("Account unverified. Sending OTP...");
          setShowOtpScreen(true);
          await fetchAndDisplayOtp(mobile);
        }
      } else {
        if (data.message === "Customer account is inactive.") {
          toast.warning("Account inactive. Verifying OTP...");
          setShowOtpScreen(true);
          await fetchAndDisplayOtp(mobile);
        } else {
          toast.error(data.message || "Login failed");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------ Register ------------------
  const handleRegister = async (e) => {
    e.preventDefault();
    const mobileRegex = /^\d{10}$/;

    if (!fullName || !mobile || !email || !password || !confirmPassword) {
      toast.error("All fields are required");
      return;
    }
    if (!mobileRegex.test(mobile)) {
      toast.error("Enter valid mobile number");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(SummaryApi.customerRegister.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: SummaryApi.bearerToken,
        },
        body: JSON.stringify({
          full_name: fullName,
          mobile_number: mobile,
          email,
          latitude: "22.5744",
          longitude: "88.3629",
          password,
        }),
      });

      const data = await response.json();

      if (data.status) {
        toast.success("Registration successful!");
        setShowOtpScreen(true);
        await fetchAndDisplayOtp(mobile);
      } else {
        toast.error(data.message || "Registration failed.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------ OTP Handlers ------------------
  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    const updatedOtp = [...otp];
    updatedOtp[index] = value[0] || "";
    setOtp(updatedOtp);
    if (value && index < 3) document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      toast.error("Enter valid 4-digit OTP");
      return;
    }
    setIsLoading(true);

    try {
      const response = await fetch(SummaryApi.customerVerifyOtp.url, {
        method: SummaryApi.customerVerifyOtp.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: SummaryApi.bearerToken,
        },
        body: JSON.stringify({ mobile_number: mobile, otp: otpValue }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Verified successfully!");
        sessionStorage.setItem("customer_id", data.customer_id || "");
        sessionStorage.setItem("customer_name", fullName);
        sessionStorage.setItem("isLoggedIn", "true");
        
        setShowOtpScreen(false);
        setIsRegister(false);
        setTimeout(() => {
          navigate("/");
          window.location.reload();
        }, 500);
      } else {
        toast.error(data.message || "Incorrect OTP");
      }
    } catch (err) {
      console.error(err);
      toast.error("Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-[5%] bg-gray-100 flex items-center justify-center px-4 relative">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-xl grid grid-cols-1 lg:grid-cols-2 overflow-hidden py-[5%] px-[2%]">
        <div className="p-8">
          {!showOtpScreen ? (
            <>
              <h2 className="text-2xl font-bold text-center text-blue-900 mb-6">{isRegister ? "Register" : "Login"}</h2>
              <form onSubmit={isRegister ? handleRegister : handleLogin} className="space-y-5">
                {isRegister && (
                  <>
                    <div><input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full px-4 py-3 border rounded-xl" placeholder="Full Name" /></div>
                    <div><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 border rounded-xl" placeholder="Email" /></div>
                  </>
                )}
                
                <div>
                  <input type="text" value={mobile} maxLength={10} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} className="w-full px-4 py-3 border rounded-xl" placeholder="Mobile Number" />
                </div>

                <div className="relative">
                  <input 
                    type={showPassword ? "text" : "password"} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    className="w-full px-4 py-3 border rounded-xl pr-10"
                    placeholder="Password" 
                  />
                  <div 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-blue-900"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                  </div>
                </div>

                {isRegister && (
                  <div className="relative">
                    <input 
                      type={showConfirmPassword ? "text" : "password"} 
                      value={confirmPassword} 
                      onChange={(e) => setConfirmPassword(e.target.value)} 
                      className="w-full px-4 py-3 border rounded-xl pr-10"
                      placeholder="Confirm Password" 
                    />
                    <div 
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-blue-900"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                    </div>
                  </div>
                )}
                
                <button type="submit" className="w-full bg-blue-900 text-white py-3 rounded-xl disabled:opacity-50" disabled={isLoading}>
                  {isLoading ? "Processing..." : (isRegister ? "Register" : "Login")}
                </button>
              </form>
              <p className="text-center mt-4 text-gray-600">
                {isRegister ? "Already have an account? " : "Don't have an account? "}
                <span className="text-blue-900 font-semibold cursor-pointer hover:underline" onClick={() => !isLoading && setIsRegister(!isRegister)}>
                  {isRegister ? "Login here" : "Register here"}
                </span>
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-center text-blue-900 mb-6">OTP Verification</h2>
              <form onSubmit={handleOtpSubmit} className="space-y-5">
                <div className="flex justify-between gap-3">
                  {otp.map((val, idx) => (
                    <input key={idx} id={`otp-${idx}`} type="text" maxLength={1} value={val} onChange={(e) => handleOtpChange(e, idx)} className="w-16 h-16 text-center text-2xl border rounded-xl" />
                  ))}
                </div>
                <button type="submit" className="w-full bg-blue-900 text-white py-3 rounded-xl disabled:opacity-50" disabled={isLoading}>{isLoading ? "Verifying..." : "Submit OTP"}</button>
                <div className="text-center mt-4">
                  <button type="button" className="text-sm text-blue-900 underline" onClick={() => fetchAndDisplayOtp(mobile)}>Resend OTP</button>
                </div>
              </form>
            </>
          )}
        </div>
        <div className="hidden lg:flex items-center justify-center bg-blue-50">
          <img src={LoginImage} alt="Login" className="w-full h-full object-contain" />
        </div>
      </div>
    </div>
  );
};

export default Login;